import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

// import SvgIcon from '@/assets/svgIcon';
import { CancelableDialog } from '@/components/Dialog';
import { useContextModal } from '@/components/Modal';
import { Select } from '@/components/Select';
// import Tooltip from '@/components/Tooltip';
import { useFetchClientListForPanel } from '@/services';
import {
  fetchAllMessages,
  useUpdateGroupTitle,
  useUpdateToClosed,
  useUpdateToProceeding,
} from '@/services/chatService';
import { chatKey } from '@/services/queryKey';

import { GroupStatus, STATUS_TEXT } from '../constants';
import { useChatStore } from '../store';
import { generateChatExcel } from '../utils';
import SvgIcon from '@/assets/svgIcon';

const totalStatusOptions = Object.values(GroupStatus).map((value) => {
  return {
    value,
    label: <p className="text-[#1c1c1e]">{STATUS_TEXT[value]}</p>,
  };
});

enum MODE {
  EDIT_MODE = 'EDIT_MODE',
  NONEDIT_MODE = 'NONEDIT_MODE',
}

interface Props {
  getVenueIds: () => string | undefined;
}

export const ChatHeader = observer(({ getVenueIds }: Props) => {
  const chatStore = useChatStore();
  const queryClient = useQueryClient();
  const { venueId } = useParams();
  const [mode, setMode] = useState<MODE>(MODE.NONEDIT_MODE);
  const [value, setValue] = useState(chatStore.currentGroup?.title);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const modal = useContextModal();
  const titleRef = useRef<HTMLInputElement>(null);
  const computedVenueId = venueId
    ? Number(venueId)
    : chatStore.currentGroup.venueId;

  const { data: clientPanel } = useFetchClientListForPanel();

  const getClientName = (): string => {
    let clientName = '';
    clientPanel?.content?.forEach((client) =>
      client.venues.forEach((venue) => {
        if (venue.id === chatStore.currentGroup.venueId) {
          clientName = client.name;
        }
      })
    );
    return clientName;
  };

  const getStatusOptions = (status) => {
    switch (status) {
      case GroupStatus.WAITING:
        return totalStatusOptions.slice(1, 3);
      case GroupStatus.PROCEEDING:
        return totalStatusOptions.slice(2, 3);
      case GroupStatus.CLOSED:
        return totalStatusOptions;
      default:
    }
  };

  const { mutate: updateToProceeding } = useUpdateToProceeding({
    groupId: chatStore.currentGroup?.id,
    venueId: computedVenueId,
    onSuccess: function () {
      if (venueId) {
        queryClient.invalidateQueries(chatKey.fetchGroups(computedVenueId));
      } else {
        queryClient.invalidateQueries(
          chatKey.fetchMultiChannelGroups(getVenueIds())
        );
      }
      chatStore.currentGroup.status = GroupStatus.PROCEEDING;
    },
    onError: function (err) {
      console.log(err);
    },
  });
  const { mutate: updateToClosed } = useUpdateToClosed({
    groupId: chatStore.currentGroup?.id,
    venueId: computedVenueId,
    onSuccess: function () {
      if (venueId) {
        queryClient.invalidateQueries(chatKey.fetchGroups(computedVenueId));
      } else {
        queryClient.invalidateQueries(
          chatKey.fetchMultiChannelGroups(getVenueIds())
        );
      }
      chatStore.currentGroup.status = GroupStatus.CLOSED;

      // 비동기 요약 생성 완료 후 다시 조회
      setTimeout(() => {
        if (venueId) {
          queryClient.invalidateQueries(chatKey.fetchGroups(computedVenueId));
        } else {
          queryClient.invalidateQueries(
            chatKey.fetchMultiChannelGroups(getVenueIds())
          );
        }
      }, 5000);
    },
    onError: function (err) {
      console.log(err);
    },
  });

  const openConfirmDialog = () => {
    modal.open([
      <CancelableDialog
        onCancel={modal.close}
        onConfirm={updateToClosed}
        title="문의를 정말 완료하시겠습니까?"
        description="완료 상태로 변경되면 해당 문의에서는 더 이상 대화할 수 없습니다."
        confirmText="완료하기"
        cancelText="취소"
      />,
      { size: 'auto' },
    ]);
  };

  const { mutate: updateTitle } = useUpdateGroupTitle({
    onSuccess: function (res) {
      chatStore.currentGroup.title = res.data.title;
    },
    onError: function (err) {
      console.log(err);
    },
  });

  const handleSelectOption = (selectedOption) => {
    if (
      selectedOption ===
      totalStatusOptions.find(
        (option) => option.value === chatStore.currentGroup?.status
      )
    )
      return;
    if (selectedOption.value === GroupStatus.PROCEEDING) updateToProceeding();
    else {
      openConfirmDialog();
    }
  };
  const handleEditStart = () => {
    setValue(chatStore.currentGroup?.title);
    setMode(MODE.EDIT_MODE);
  };
  const cancelEdit = () => {
    setTimeout(() => {
      setMode(MODE.NONEDIT_MODE);
    }, 200);
  };
  useEffect(() => {
    if (mode === MODE.EDIT_MODE) {
      titleRef.current?.focus();
    }
  }, [mode]);
  const handleConfirmEdit = () => {
    if (value.trim() === '') {
      setValue(chatStore.currentGroup.title);
    } else if (value !== chatStore.currentGroup.title) {
      updateTitle({
        groupId: chatStore.currentGroup.id,
        venueId: computedVenueId,
        title: value,
      });
    }
    setMode(MODE.NONEDIT_MODE);
  };

  const handleInputChange = (e) => {
    setValue(e.target.value);
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadExcel = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const messages = await fetchAllMessages({
        venueId: computedVenueId,
        groupId: chatStore.currentGroup.id,
      });
      const clientName = getClientName();
      await generateChatExcel(
        messages,
        chatStore.currentGroup.title,
        clientName
      );
    } catch (error) {
      console.error('다운로드 실패:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    setValue(chatStore.currentGroup?.title);
  }, [chatStore.currentGroup]);
  const showSummary =
    chatStore.currentGroup?.status === GroupStatus.CLOSED &&
    !!chatStore.currentGroup?.summary;

  if (!venueId && chatStore.groupList.length === 0) return <></>;
  return (
    <div className="border-b-[1px] border-solid border-[#ececec] bg-[#f9fafacc] px-8">
      <div className="flex h-20 items-center gap-1 justify-between">
      <div className="flex flex-1 items-center overflow-hidden gap-3 text-xl font-semibold text-[#171717]">
        {chatStore.currentGroup?.id !== -1 && (
          <p className="flex h-8 items-center rounded bg-[#f5f5f5] px-2 text-base font-medium text-[#8b8b8b]">
            {`#${chatStore.currentGroup?.id}`}
          </p>
        )}
        {mode === MODE.EDIT_MODE && (
          <input
            ref={titleRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onBlur={cancelEdit}
            className="truncate flex-1 rounded-lg h-12"
          />
        )}
        {mode === MODE.NONEDIT_MODE && (
          <p className="truncate flex-1">{chatStore.currentGroup?.title}</p>
        )}
      </div>
      <div className="flex min-w-[130px] items-center gap-3">
        {chatStore.currentGroup.id !== -1 && (
          <>
            {mode === MODE.NONEDIT_MODE && (
              <button
                onClick={handleEditStart}
                className="rounded-lg p-1 hover:bg-gray-200"
              >
                <SvgIcon name="editIcon" width={28} height={28} />
              </button>
            )}
            {mode === MODE.EDIT_MODE && (
              <button
                onClick={handleConfirmEdit}
                className="rounded-lg p-1 hover:bg-gray-200"
              >
                <SvgIcon name="checkIcon" width={28} height={28} />
              </button>
            )}
            <Select
              disabled={chatStore.currentGroup?.status === GroupStatus.CLOSED}
              options={getStatusOptions(chatStore.currentGroup?.status) || []}
              onSelect={handleSelectOption}
              currentOption={totalStatusOptions.find(
                (option) => option.value === chatStore.currentGroup?.status
              )}
              showSelectedIcon={false}
              buttonStyles={'flex w-24 h-9 items-center'}
            />
            <button
              onClick={handleDownloadExcel}
              disabled={isDownloading}
              className="flex h-10 items-center gap-1 whitespace-nowrap rounded-lg text-sm font-medium text-[#4b5563] hover:bg-gray-200 disabled:opacity-50"
              title="대화내용 다운로드"
            >
              <SvgIcon name="downloadIcon" width={20} height={20} />
              <span className="hidden xl:inline">
                {isDownloading ? '다운로드중...' : '다운로드'}
              </span>
            </button>
          </>
        )}
      </div>
      </div>
      {showSummary && (
        <div className="pb-3">
          <button
            onClick={() => setIsSummaryOpen(!isSummaryOpen)}
            className="flex items-center gap-1 text-sm font-medium text-[#6b7280] hover:text-[#374151]"
          >
            <span>{isSummaryOpen ? '\u25BC' : '\u25B6'}</span>
            <span>AI 요약</span>
          </button>
          {isSummaryOpen && (
            <p className="mt-1 rounded-lg bg-[#f3f4f6] px-4 py-3 text-sm leading-relaxed text-[#374151]">
              {chatStore.currentGroup.summary}
            </p>
          )}
        </div>
      )}
    </div>
  );
});
